import * as T from './clickHouseConstants';
import type { OrderSource } from './clickHouseApi';
import type {
  ClickHouseData,
  CompByStaffItem,
  CompByStaffResponse,
  CompOrderLogItem,
  LiveCategorySalesItem,
  CompOrderLogResponse,
  CompReasonsResponse,
  LabourEmployeesResponse,
  LabourEmployeeSalesResponse,
  LabourKpiComparisonResponse,
  LabourStaffPerformanceResponse,
  LabourTipsResponse,
  LabourVoidsResponse,
  LabourWorkingHoursLogResponse,
  LabourWorkingHoursResponse,
  LiveByChannelItem,
  LiveCategorySalesResponse,
  LiveHourlySalesItem,
  LiveItemSalesV2Item,
  LiveOutstandingValueResponse,
  LivePaymentsItem,
  LiveRefundDetailsResponse,
  RefundLogResponse,
  RefundReasonsResponse,
  RefundByStaffResponse,
  RefundComponentSplitResponse,
  LiveRefundSummaryItem,
  LiveStaffActiveClockingItem,
  LiveStaffActiveClockingV2Response,
  LiveStaffPerformanceItem,
  LiveStaffRefundsItem,
  LiveVoidByStaffItem,
  LiveVoidDetailsResponse,
  LiveVoidKpisResponse,
  LiveVoidSummaryItem,
  OrderChannelGroup,
  OrderDetailResponse,
  OrderLogResponse,
  SalesByChannelBreakdownResponse,
  SnapshotComparisonResponse,
  SnapshotDetailsResponse,
  UnpaidOrdersResponse,
  HourlySalesResponse
} from '../../../interface/clickHouseInterface';

export interface OrderLogRequestParams {
  locationId: string;
  page: number;
  size: number;
  sortBy: string;
  sortDir: string;
  search?: string;
  channelName?: string;
  /** Sales-channel filter used by the Sales by Channel breakdown drill-down. */
  channelGroup?: OrderChannelGroup;
  guestCount?: number;
  minAmount?: number;
  maxAmount?: number;
  startDate?: string;
  endDate?: string;
  orderSource?: OrderSource;
  /** 'cancel' switches the log to cancelled orders (ORD-1.3) */
  type?: 'cancel';
}

/** CANC-1 — /order-log narrowed to cancelled orders. `type` is always 'cancel'. */
export interface CancelledOrderLogRequestParams {
  locationId: string;
  page: number;
  size: number;
  sortBy: string;
  sortDir: string;
  startDate?: string;
  endDate?: string;
  orderSource?: OrderSource;
  type: 'cancel';
}

export interface ExportOrderLogRequestParams {
  locationId: string;
  page: number;
  size: number;
  sortBy: string;
  sortDir: string;
  search?: string;
  channelName?: string;
  /** Sales-channel filter used by the Sales by Channel breakdown drill-down. */
  channelGroup?: OrderChannelGroup;
  guestCount?: number;
  minAmount?: number;
  maxAmount?: number;
  startDate?: string;
  endDate?: string;
  orderSource?: OrderSource;
  format: 'csv' | 'excel' | 'pdf';
  filename: string;
  currencySymbol: string;
}

export const fetchClickHouseRequest = (locationId: string) => ({
  type: T.FETCH_CLICKHOUSE_REQUEST as typeof T.FETCH_CLICKHOUSE_REQUEST,
  payload: { locationId },
});

export const fetchClickHouseSuccess = (data: ClickHouseData) => ({
  type: T.FETCH_CLICKHOUSE_SUCCESS as typeof T.FETCH_CLICKHOUSE_SUCCESS,
  payload: { data },
});

export const fetchClickHouseFailure = (error: string) => ({
  type: T.FETCH_CLICKHOUSE_FAILURE as typeof T.FETCH_CLICKHOUSE_FAILURE,
  payload: { error },
});

export const fetchSnapshotDetailsRequest = (locationId: string, startDate?: string, endDate?: string, orderSource?: OrderSource) => ({
  type: T.FETCH_SNAPSHOT_DETAILS_REQUEST as typeof T.FETCH_SNAPSHOT_DETAILS_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchSnapshotDetailsSuccess = (data: SnapshotDetailsResponse) => ({
  type: T.FETCH_SNAPSHOT_DETAILS_SUCCESS as typeof T.FETCH_SNAPSHOT_DETAILS_SUCCESS,
  payload: data,
});

export const fetchSnapshotDetailsFailure = (error: string) => ({
  type: T.FETCH_SNAPSHOT_DETAILS_FAILURE as typeof T.FETCH_SNAPSHOT_DETAILS_FAILURE,
  payload: { error },
});

export const fetchLiveItemSalesV2Request = (locationId: string, page: number = 0, size: number = 10, pinnedItemNames?: string[], startDate?: string, endDate?: string, orderSource?: OrderSource) => ({
  type: T.FETCH_LIVE_ITEM_SALES_V2_REQUEST as typeof T.FETCH_LIVE_ITEM_SALES_V2_REQUEST,
  payload: { locationId, page, size, pinnedItemNames, startDate, endDate, orderSource },
});

export const fetchLiveItemSalesV2Success = (data: { content: LiveItemSalesV2Item[]; totalElements: number; totalPages: number }) => ({
  type: T.FETCH_LIVE_ITEM_SALES_V2_SUCCESS as typeof T.FETCH_LIVE_ITEM_SALES_V2_SUCCESS,
  payload: data,
});

export const fetchLiveItemSalesV2Failure = (error: string) => ({
  type: T.FETCH_LIVE_ITEM_SALES_V2_FAILURE as typeof T.FETCH_LIVE_ITEM_SALES_V2_FAILURE,
  payload: { error },
});

export const fetchLiveByChannelRequest = ({ locationId, startDate, endDate, orderSource }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource }) => ({
  type: T.FETCH_LIVE_BY_CHANNEL_REQUEST as typeof T.FETCH_LIVE_BY_CHANNEL_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchLiveByChannelSuccess = (data: LiveByChannelItem[]) => ({
  type: T.FETCH_LIVE_BY_CHANNEL_SUCCESS as typeof T.FETCH_LIVE_BY_CHANNEL_SUCCESS,
  payload: data,
});

export const fetchLiveByChannelFailure = (error: string) => ({
  type: T.FETCH_LIVE_BY_CHANNEL_FAILURE as typeof T.FETCH_LIVE_BY_CHANNEL_FAILURE,
  payload: { error },
});

// Third-Party Marketplace Performance card — always unfiltered, so no orderSource param (SSE-16)
export const fetchThirdPartyByChannelRequest = ({ locationId, startDate, endDate }: { locationId: string; startDate?: string; endDate?: string }) => ({
  type: T.FETCH_THIRD_PARTY_BY_CHANNEL_REQUEST as typeof T.FETCH_THIRD_PARTY_BY_CHANNEL_REQUEST,
  payload: { locationId, startDate, endDate },
});

export const fetchThirdPartyByChannelSuccess = (data: LiveByChannelItem[]) => ({
  type: T.FETCH_THIRD_PARTY_BY_CHANNEL_SUCCESS as typeof T.FETCH_THIRD_PARTY_BY_CHANNEL_SUCCESS,
  payload: data,
});

export const fetchThirdPartyByChannelFailure = (error: string) => ({
  type: T.FETCH_THIRD_PARTY_BY_CHANNEL_FAILURE as typeof T.FETCH_THIRD_PARTY_BY_CHANNEL_FAILURE,
  payload: { error },
});

export const fetchLivePaymentsRequest = ({ locationId, startDate, endDate, orderSource }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource }) => ({
  type: T.FETCH_LIVE_PAYMENTS_REQUEST as typeof T.FETCH_LIVE_PAYMENTS_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchLivePaymentsSuccess = (data: LivePaymentsItem[]) => ({
  type: T.FETCH_LIVE_PAYMENTS_SUCCESS as typeof T.FETCH_LIVE_PAYMENTS_SUCCESS,
  payload: data,
});

export const fetchLivePaymentsFailure = (error: string) => ({
  type: T.FETCH_LIVE_PAYMENTS_FAILURE as typeof T.FETCH_LIVE_PAYMENTS_FAILURE,
  payload: { error },
});

export const fetchLiveRefundSummaryRequest = (locationId: string, startDate?: string, endDate?: string, orderSource?: OrderSource) => ({
  type: T.FETCH_LIVE_REFUND_SUMMARY_REQUEST as typeof T.FETCH_LIVE_REFUND_SUMMARY_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchLiveRefundSummarySuccess = (data: LiveRefundSummaryItem[]) => ({
  type: T.FETCH_LIVE_REFUND_SUMMARY_SUCCESS as typeof T.FETCH_LIVE_REFUND_SUMMARY_SUCCESS,
  payload: data,
});

export const fetchLiveRefundSummaryFailure = (error: string) => ({
  type: T.FETCH_LIVE_REFUND_SUMMARY_FAILURE as typeof T.FETCH_LIVE_REFUND_SUMMARY_FAILURE,
  payload: { error },
});

export const fetchLiveRefundDetailsRequest = (locationId: string, page: number, size: number, orderSource?: OrderSource, startDate?: string, endDate?: string) => ({
  type: T.FETCH_LIVE_REFUND_DETAILS_REQUEST as typeof T.FETCH_LIVE_REFUND_DETAILS_REQUEST,
  payload: { locationId, page, size, orderSource, startDate, endDate },
});

export const fetchLiveRefundDetailsSuccess = (data: LiveRefundDetailsResponse) => ({
  type: T.FETCH_LIVE_REFUND_DETAILS_SUCCESS as typeof T.FETCH_LIVE_REFUND_DETAILS_SUCCESS,
  payload: data,
});

export const fetchLiveRefundDetailsFailure = (error: string) => ({
  type: T.FETCH_LIVE_REFUND_DETAILS_FAILURE as typeof T.FETCH_LIVE_REFUND_DETAILS_FAILURE,
  payload: { error },
});

// REF-2 — the Refund Log's real data source (GET /sales/live/v2/refund-log).
export const fetchRefundLogRequest = (locationId: string, startDate?: string, endDate?: string, orderSource?: OrderSource, page: number = 1, size: number = 500) => ({
  type: T.FETCH_REFUND_LOG_REQUEST as typeof T.FETCH_REFUND_LOG_REQUEST,
  payload: { locationId, startDate, endDate, orderSource, page, size },
});

export const fetchRefundLogSuccess = (data: RefundLogResponse) => ({
  type: T.FETCH_REFUND_LOG_SUCCESS as typeof T.FETCH_REFUND_LOG_SUCCESS,
  payload: data,
});

export const fetchRefundLogFailure = (error: string) => ({
  type: T.FETCH_REFUND_LOG_FAILURE as typeof T.FETCH_REFUND_LOG_FAILURE,
  payload: { error },
});

// REF-6 — the Refund Reasons card's real data source (GET /sales/live/v2/refund-reasons).
// `page` is 1-indexed, `size` defaults to 10 server-side. `keepPrevious` is for page/size changes:
// the reducer then keeps the rows on screen while the next page loads instead of clearing them.
export const fetchRefundReasonsRequest = (locationId: string, startDate?: string, endDate?: string, orderSource?: OrderSource, page?: number, size?: number, keepPrevious?: boolean) => ({
  type: T.FETCH_REFUND_REASONS_REQUEST as typeof T.FETCH_REFUND_REASONS_REQUEST,
  payload: { locationId, startDate, endDate, orderSource, page, size, keepPrevious },
});

export const fetchRefundReasonsSuccess = (data: RefundReasonsResponse) => ({
  type: T.FETCH_REFUND_REASONS_SUCCESS as typeof T.FETCH_REFUND_REASONS_SUCCESS,
  payload: data,
});

export const fetchRefundReasonsFailure = (error: string) => ({
  type: T.FETCH_REFUND_REASONS_FAILURE as typeof T.FETCH_REFUND_REASONS_FAILURE,
  payload: { error },
});

// REF-6 — the Refunds by Staff card's real data source (GET /sales/live/v2/refund-by-staff).
export const fetchRefundByStaffRequest = (locationId: string, startDate?: string, endDate?: string, orderSource?: OrderSource, page?: number, size?: number, keepPrevious?: boolean) => ({
  type: T.FETCH_REFUND_BY_STAFF_REQUEST as typeof T.FETCH_REFUND_BY_STAFF_REQUEST,
  payload: { locationId, startDate, endDate, orderSource, page, size, keepPrevious },
});

export const fetchRefundByStaffSuccess = (data: RefundByStaffResponse) => ({
  type: T.FETCH_REFUND_BY_STAFF_SUCCESS as typeof T.FETCH_REFUND_BY_STAFF_SUCCESS,
  payload: data,
});

export const fetchRefundByStaffFailure = (error: string) => ({
  type: T.FETCH_REFUND_BY_STAFF_FAILURE as typeof T.FETCH_REFUND_BY_STAFF_FAILURE,
  payload: { error },
});

// REF-1 — the Component Split card's real data source (GET /sales/live/v2/refund-component-split).
export const fetchRefundComponentSplitRequest = (locationId: string, startDate?: string, endDate?: string, orderSource?: OrderSource) => ({
  type: T.FETCH_REFUND_COMPONENT_SPLIT_REQUEST as typeof T.FETCH_REFUND_COMPONENT_SPLIT_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchRefundComponentSplitSuccess = (data: RefundComponentSplitResponse) => ({
  type: T.FETCH_REFUND_COMPONENT_SPLIT_SUCCESS as typeof T.FETCH_REFUND_COMPONENT_SPLIT_SUCCESS,
  payload: data,
});

export const fetchRefundComponentSplitFailure = (error: string) => ({
  type: T.FETCH_REFUND_COMPONENT_SPLIT_FAILURE as typeof T.FETCH_REFUND_COMPONENT_SPLIT_FAILURE,
  payload: { error },
});

export const fetchLiveVoidSummaryRequest = (locationId: string, startDate?: string, endDate?: string, orderSource?: OrderSource) => ({
  type: T.FETCH_LIVE_VOID_SUMMARY_REQUEST as typeof T.FETCH_LIVE_VOID_SUMMARY_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchLiveVoidSummarySuccess = (data: LiveVoidSummaryItem[]) => ({
  type: T.FETCH_LIVE_VOID_SUMMARY_SUCCESS as typeof T.FETCH_LIVE_VOID_SUMMARY_SUCCESS,
  payload: data,
});

export const fetchLiveVoidSummaryFailure = (error: string) => ({
  type: T.FETCH_LIVE_VOID_SUMMARY_FAILURE as typeof T.FETCH_LIVE_VOID_SUMMARY_FAILURE,
  payload: { error },
});

export const fetchVoidByStaffRequest = (locationId: string, startDate?: string, endDate?: string, orderSource?: OrderSource) => ({
  type: T.FETCH_VOID_BY_STAFF_REQUEST as typeof T.FETCH_VOID_BY_STAFF_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchVoidByStaffSuccess = (data: LiveVoidByStaffItem[]) => ({
  type: T.FETCH_VOID_BY_STAFF_SUCCESS as typeof T.FETCH_VOID_BY_STAFF_SUCCESS,
  payload: data,
});

export const fetchVoidByStaffFailure = (error: string) => ({
  type: T.FETCH_VOID_BY_STAFF_FAILURE as typeof T.FETCH_VOID_BY_STAFF_FAILURE,
  payload: { error },
});

export const fetchVoidKpisRequest = (locationId: string, startDate?: string, endDate?: string, orderSource?: OrderSource) => ({
  type: T.FETCH_VOID_KPIS_REQUEST as typeof T.FETCH_VOID_KPIS_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchVoidKpisSuccess = (data: LiveVoidKpisResponse) => ({
  type: T.FETCH_VOID_KPIS_SUCCESS as typeof T.FETCH_VOID_KPIS_SUCCESS,
  payload: data,
});

export const fetchVoidKpisFailure = (error: string) => ({
  type: T.FETCH_VOID_KPIS_FAILURE as typeof T.FETCH_VOID_KPIS_FAILURE,
  payload: { error },
});

export const fetchLiveVoidDetailsRequest = (locationId: string, page: number, size: number, startDate?: string, endDate?: string, orderSource?: OrderSource) => ({
  type: T.FETCH_LIVE_VOID_DETAILS_REQUEST as typeof T.FETCH_LIVE_VOID_DETAILS_REQUEST,
  payload: { locationId, page, size, startDate, endDate, orderSource },
});

export const fetchLiveVoidDetailsSuccess = (data: LiveVoidDetailsResponse) => ({
  type: T.FETCH_LIVE_VOID_DETAILS_SUCCESS as typeof T.FETCH_LIVE_VOID_DETAILS_SUCCESS,
  payload: data,
});

export const fetchLiveVoidDetailsFailure = (error: string) => ({
  type: T.FETCH_LIVE_VOID_DETAILS_FAILURE as typeof T.FETCH_LIVE_VOID_DETAILS_FAILURE,
  payload: { error },
});

export const fetchLiveHourlySalesRequest = (locationId: string, orderSource?: OrderSource) => ({
  type: T.FETCH_LIVE_HOURLY_SALES_REQUEST as typeof T.FETCH_LIVE_HOURLY_SALES_REQUEST,
  payload: { locationId, orderSource },
});

export const fetchLiveHourlySalesSuccess = (data: LiveHourlySalesItem[]) => ({
  type: T.FETCH_LIVE_HOURLY_SALES_SUCCESS as typeof T.FETCH_LIVE_HOURLY_SALES_SUCCESS,
  payload: data,
});

export const fetchLiveHourlySalesFailure = (error: string) => ({
  type: T.FETCH_LIVE_HOURLY_SALES_FAILURE as typeof T.FETCH_LIVE_HOURLY_SALES_FAILURE,
  payload: { error },
});

export const fetchHourlySalesRequest = ({ locationId, startDate, endDate, orderSource }: { locationId: string; startDate: string; endDate: string; orderSource?: OrderSource }) => ({
  type: T.FETCH_HOURLY_SALES_REQUEST as typeof T.FETCH_HOURLY_SALES_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchHourlySalesSuccess = (data: HourlySalesResponse) => ({
  type: T.FETCH_HOURLY_SALES_SUCCESS as typeof T.FETCH_HOURLY_SALES_SUCCESS,
  payload: data,
});

export const fetchHourlySalesFailure = (error: string) => ({
  type: T.FETCH_HOURLY_SALES_FAILURE as typeof T.FETCH_HOURLY_SALES_FAILURE,
  payload: { error },
});

export const fetchLiveStaffPerformanceRequest = ({ locationId, startDate, endDate, orderSource }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource }) => ({
  type: T.FETCH_LIVE_STAFF_PERFORMANCE_REQUEST as typeof T.FETCH_LIVE_STAFF_PERFORMANCE_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchLiveStaffPerformanceSuccess = (data: LiveStaffPerformanceItem[]) => ({
  type: T.FETCH_LIVE_STAFF_PERFORMANCE_SUCCESS as typeof T.FETCH_LIVE_STAFF_PERFORMANCE_SUCCESS,
  payload: data,
});

export const fetchLiveStaffPerformanceFailure = (error: string) => ({
  type: T.FETCH_LIVE_STAFF_PERFORMANCE_FAILURE as typeof T.FETCH_LIVE_STAFF_PERFORMANCE_FAILURE,
  payload: { error },
});

export const fetchLiveStaffActiveClockingRequest = (locationId: string, orderSource?: OrderSource, startDate?: string, endDate?: string) => ({
  type: T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_REQUEST as typeof T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_REQUEST,
  payload: { locationId, orderSource, startDate, endDate },
});

export const fetchLiveStaffActiveClockingSuccess = (data: LiveStaffActiveClockingItem[]) => ({
  type: T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_SUCCESS as typeof T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_SUCCESS,
  payload: data,
});

export const fetchLiveStaffActiveClockingFailure = (error: string) => ({
  type: T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_FAILURE as typeof T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_FAILURE,
  payload: { error },
});

// Staff Roster — GET /sales/live/v2/staff-active-clocking-v2
export const fetchLiveStaffActiveClockingV2Request = (params: { locationId: string; startDate?: string; endDate?: string; search?: string; page?: number; size?: number; sortBy?: string; sortAsc?: boolean; orderSource?: OrderSource }) => ({
  type: T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_V2_REQUEST as typeof T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_V2_REQUEST,
  payload: params,
});

export const fetchLiveStaffActiveClockingV2Success = (data: LiveStaffActiveClockingV2Response) => ({
  type: T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_V2_SUCCESS as typeof T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_V2_SUCCESS,
  payload: data,
});

export const fetchLiveStaffActiveClockingV2Failure = (error: string) => ({
  type: T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_V2_FAILURE as typeof T.FETCH_LIVE_STAFF_ACTIVE_CLOCKING_V2_FAILURE,
  payload: { error },
});

export const fetchOrderLogRequest = (params: OrderLogRequestParams) => ({
  type: T.FETCH_ORDER_LOG_REQUEST as typeof T.FETCH_ORDER_LOG_REQUEST,
  payload: params,
});

export const fetchOrderLogSuccess = (data: OrderLogResponse) => ({
  type: T.FETCH_ORDER_LOG_SUCCESS as typeof T.FETCH_ORDER_LOG_SUCCESS,
  payload: data,
});

export const fetchOrderLogFailure = (error: string) => ({
  type: T.FETCH_ORDER_LOG_FAILURE as typeof T.FETCH_ORDER_LOG_FAILURE,
  payload: { error },
});

export const fetchCancelledOrderLogRequest = (params: CancelledOrderLogRequestParams) => ({
  type: T.FETCH_CANCELLED_ORDER_LOG_REQUEST as typeof T.FETCH_CANCELLED_ORDER_LOG_REQUEST,
  payload: params,
});

export const fetchCancelledOrderLogSuccess = (data: OrderLogResponse) => ({
  type: T.FETCH_CANCELLED_ORDER_LOG_SUCCESS as typeof T.FETCH_CANCELLED_ORDER_LOG_SUCCESS,
  payload: data,
});

export const fetchCancelledOrderLogFailure = (error: string) => ({
  type: T.FETCH_CANCELLED_ORDER_LOG_FAILURE as typeof T.FETCH_CANCELLED_ORDER_LOG_FAILURE,
  payload: { error },
});

export const exportOrderLogRequest = (params: ExportOrderLogRequestParams) => ({
  type: T.EXPORT_ORDER_LOG_REQUEST as typeof T.EXPORT_ORDER_LOG_REQUEST,
  payload: params,
});

export const exportOrderLogSuccess = () => ({
  type: T.EXPORT_ORDER_LOG_SUCCESS as typeof T.EXPORT_ORDER_LOG_SUCCESS,
});

export const exportOrderLogFailure = (error: string) => ({
  type: T.EXPORT_ORDER_LOG_FAILURE as typeof T.EXPORT_ORDER_LOG_FAILURE,
  payload: { error },
});

export const exportOrderLogReset = () => ({
  type: T.EXPORT_ORDER_LOG_RESET as typeof T.EXPORT_ORDER_LOG_RESET,
});

export const fetchOrderDetailRequest = (params: { locationId: string; orderId: string; type?: 'historical' }) => ({
  type: T.FETCH_ORDER_DETAIL_REQUEST as typeof T.FETCH_ORDER_DETAIL_REQUEST,
  payload: params,
});

export const fetchOrderDetailSuccess = (data: OrderDetailResponse) => ({
  type: T.FETCH_ORDER_DETAIL_SUCCESS as typeof T.FETCH_ORDER_DETAIL_SUCCESS,
  payload: data,
});

export const fetchOrderDetailFailure = (error: string) => ({
  type: T.FETCH_ORDER_DETAIL_FAILURE as typeof T.FETCH_ORDER_DETAIL_FAILURE,
  payload: { error },
});

export const clearOrderDetail = () => ({
  type: T.CLEAR_ORDER_DETAIL as typeof T.CLEAR_ORDER_DETAIL,
});

export const fetchLiveUnpaidOrdersRequest = ({ locationId, size = 10, startDate, endDate, orderSource }: { locationId: string; size?: number; startDate?: string; endDate?: string; orderSource?: OrderSource }) => ({
  type: T.FETCH_LIVE_UNPAID_ORDERS_REQUEST as typeof T.FETCH_LIVE_UNPAID_ORDERS_REQUEST,
  payload: { locationId, size, startDate, endDate, orderSource },
});

export const fetchLiveUnpaidOrdersSuccess = (data: UnpaidOrdersResponse) => ({
  type: T.FETCH_LIVE_UNPAID_ORDERS_SUCCESS as typeof T.FETCH_LIVE_UNPAID_ORDERS_SUCCESS,
  payload: data,
});

export const fetchLiveUnpaidOrdersFailure = (error: string) => ({
  type: T.FETCH_LIVE_UNPAID_ORDERS_FAILURE as typeof T.FETCH_LIVE_UNPAID_ORDERS_FAILURE,
  payload: { error },
});

export const fetchLiveUnpaidLogRequest = (params: { locationId: string; page: number; size: number; startDate?: string; endDate?: string; sortBy?: string; sortDir?: 'asc' | 'desc'; search?: string; orderType?: string; orderSource?: OrderSource }) => ({
  type: T.FETCH_LIVE_UNPAID_LOG_REQUEST as typeof T.FETCH_LIVE_UNPAID_LOG_REQUEST,
  payload: params,
});

export const fetchLiveUnpaidLogSuccess = (data: UnpaidOrdersResponse) => ({
  type: T.FETCH_LIVE_UNPAID_LOG_SUCCESS as typeof T.FETCH_LIVE_UNPAID_LOG_SUCCESS,
  payload: data,
});

export const fetchLiveUnpaidLogFailure = (error: string) => ({
  type: T.FETCH_LIVE_UNPAID_LOG_FAILURE as typeof T.FETCH_LIVE_UNPAID_LOG_FAILURE,
  payload: { error },
});

// ── Complimentary Orders ────────────────────────────────────────

export const fetchLiveCompOrderLogRequest = (params: { locationId: string; page: number; size: number; startDate?: string; endDate?: string; orderSource?: OrderSource }) => ({
  type: T.FETCH_LIVE_COMP_ORDER_LOG_REQUEST as typeof T.FETCH_LIVE_COMP_ORDER_LOG_REQUEST,
  payload: params,
});

export const fetchLiveCompOrderLogSuccess = (data: CompOrderLogResponse) => ({
  type: T.FETCH_LIVE_COMP_ORDER_LOG_SUCCESS as typeof T.FETCH_LIVE_COMP_ORDER_LOG_SUCCESS,
  payload: data,
});

export const fetchLiveCompOrderLogFailure = (error: string) => ({
  type: T.FETCH_LIVE_COMP_ORDER_LOG_FAILURE as typeof T.FETCH_LIVE_COMP_ORDER_LOG_FAILURE,
  payload: { error },
});

export const fetchLiveCompReasonsRequest = ({ locationId, startDate, endDate, orderSource }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource }) => ({
  type: T.FETCH_LIVE_COMP_REASONS_REQUEST as typeof T.FETCH_LIVE_COMP_REASONS_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchLiveCompReasonsSuccess = (data: CompReasonsResponse) => ({
  type: T.FETCH_LIVE_COMP_REASONS_SUCCESS as typeof T.FETCH_LIVE_COMP_REASONS_SUCCESS,
  payload: data,
});

export const fetchLiveCompReasonsFailure = (error: string) => ({
  type: T.FETCH_LIVE_COMP_REASONS_FAILURE as typeof T.FETCH_LIVE_COMP_REASONS_FAILURE,
  payload: { error },
});

export const fetchLiveCompByStaffRequest = ({ locationId, startDate, endDate, orderSource }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource }) => ({
  type: T.FETCH_LIVE_COMP_BY_STAFF_REQUEST as typeof T.FETCH_LIVE_COMP_BY_STAFF_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchLiveCompByStaffSuccess = (data: CompByStaffItem[]) => ({
  type: T.FETCH_LIVE_COMP_BY_STAFF_SUCCESS as typeof T.FETCH_LIVE_COMP_BY_STAFF_SUCCESS,
  payload: data,
});

export const fetchLiveCompByStaffFailure = (error: string) => ({
  type: T.FETCH_LIVE_COMP_BY_STAFF_FAILURE as typeof T.FETCH_LIVE_COMP_BY_STAFF_FAILURE,
  payload: { error },
});

export const fetchLiveCategorySalesRequest = (locationId: string, startDate?: string, endDate?: string, orderSource?: OrderSource, page?: number, size?: number) => ({
  type: T.FETCH_LIVE_CATEGORY_SALES_REQUEST as typeof T.FETCH_LIVE_CATEGORY_SALES_REQUEST,
  payload: { locationId, startDate, endDate, orderSource, page, size },
});

export const fetchLiveCategorySalesSuccess = (data: LiveCategorySalesResponse) => ({
  type: T.FETCH_LIVE_CATEGORY_SALES_SUCCESS as typeof T.FETCH_LIVE_CATEGORY_SALES_SUCCESS,
  payload: data,
});

export const fetchLiveCategorySalesFailure = (error: string) => ({
  type: T.FETCH_LIVE_CATEGORY_SALES_FAILURE as typeof T.FETCH_LIVE_CATEGORY_SALES_FAILURE,
  payload: { error },
});

// Staff Refunds — GET /sales/live/v2/staff-refunds
export const fetchLiveStaffRefundsRequest = ({ locationId, startDate, endDate, orderSource }: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource }) => ({
  type: T.FETCH_LIVE_STAFF_REFUNDS_REQUEST as typeof T.FETCH_LIVE_STAFF_REFUNDS_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchLiveStaffRefundsSuccess = (data: LiveStaffRefundsItem[]) => ({
  type: T.FETCH_LIVE_STAFF_REFUNDS_SUCCESS as typeof T.FETCH_LIVE_STAFF_REFUNDS_SUCCESS,
  payload: data,
});

export const fetchLiveStaffRefundsFailure = (error: string) => ({
  type: T.FETCH_LIVE_STAFF_REFUNDS_FAILURE as typeof T.FETCH_LIVE_STAFF_REFUNDS_FAILURE,
  payload: { error },
});

// Snapshot Comparison — GET /sales/live/v2/snapshot-details/comparison
export const fetchSnapshotComparisonRequest = (locationId: string, startDate?: string, endDate?: string, orderSource?: OrderSource) => ({
  type: T.FETCH_SNAPSHOT_COMPARISON_REQUEST as typeof T.FETCH_SNAPSHOT_COMPARISON_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchSnapshotComparisonSuccess = (data: SnapshotComparisonResponse) => ({
  type: T.FETCH_SNAPSHOT_COMPARISON_SUCCESS as typeof T.FETCH_SNAPSHOT_COMPARISON_SUCCESS,
  payload: data,
});

export const fetchSnapshotComparisonFailure = (error: string) => ({
  type: T.FETCH_SNAPSHOT_COMPARISON_FAILURE as typeof T.FETCH_SNAPSHOT_COMPARISON_FAILURE,
  payload: { error },
});

// Channel Names — GET /sales/live/v2/channel-names
export const fetchChannelNamesRequest = (locationId: string) => ({
  type: T.FETCH_CHANNEL_NAMES_REQUEST as typeof T.FETCH_CHANNEL_NAMES_REQUEST,
  payload: { locationId },
});

export const fetchChannelNamesSuccess = (data: string[]) => ({
  type: T.FETCH_CHANNEL_NAMES_SUCCESS as typeof T.FETCH_CHANNEL_NAMES_SUCCESS,
  payload: data,
});

export const fetchChannelNamesFailure = (error: string) => ({
  type: T.FETCH_CHANNEL_NAMES_FAILURE as typeof T.FETCH_CHANNEL_NAMES_FAILURE,
  payload: { error },
});

export const fetchLiveOutstandingValueRequest = (locationId: string, startDate?: string, endDate?: string, orderSource?: OrderSource) => ({
  type: T.FETCH_LIVE_OUTSTANDING_VALUE_REQUEST as typeof T.FETCH_LIVE_OUTSTANDING_VALUE_REQUEST,
  payload: { locationId, startDate, endDate, orderSource },
});

export const fetchLiveOutstandingValueSuccess = (data: LiveOutstandingValueResponse) => ({
  type: T.FETCH_LIVE_OUTSTANDING_VALUE_SUCCESS as typeof T.FETCH_LIVE_OUTSTANDING_VALUE_SUCCESS,
  payload: data,
});

export const fetchLiveOutstandingValueFailure = (error: string) => ({
  type: T.FETCH_LIVE_OUTSTANDING_VALUE_FAILURE as typeof T.FETCH_LIVE_OUTSTANDING_VALUE_FAILURE,
  payload: { error },
});

// Labour Voids — GET /sales/live/v2/labour-voids
export const fetchLabourVoidsRequest = (params: { locationId: string; startDate?: string; endDate?: string; employeeId?: string }) => ({
  type: T.FETCH_LABOUR_VOIDS_REQUEST as typeof T.FETCH_LABOUR_VOIDS_REQUEST,
  payload: params,
});

export const fetchLabourVoidsSuccess = (data: LabourVoidsResponse) => ({
  type: T.FETCH_LABOUR_VOIDS_SUCCESS as typeof T.FETCH_LABOUR_VOIDS_SUCCESS,
  payload: data,
});

export const fetchLabourVoidsFailure = (error: string) => ({
  type: T.FETCH_LABOUR_VOIDS_FAILURE as typeof T.FETCH_LABOUR_VOIDS_FAILURE,
  payload: { error },
});

// Labour Tips — GET /sales/live/v2/labour-tips
export const fetchLabourTipsRequest = (params: { locationId: string; startDate?: string; endDate?: string; employeeId?: string }) => ({
  type: T.FETCH_LABOUR_TIPS_REQUEST as typeof T.FETCH_LABOUR_TIPS_REQUEST,
  payload: params,
});

export const fetchLabourTipsSuccess = (data: LabourTipsResponse) => ({
  type: T.FETCH_LABOUR_TIPS_SUCCESS as typeof T.FETCH_LABOUR_TIPS_SUCCESS,
  payload: data,
});

export const fetchLabourTipsFailure = (error: string) => ({
  type: T.FETCH_LABOUR_TIPS_FAILURE as typeof T.FETCH_LABOUR_TIPS_FAILURE,
  payload: { error },
});

// Labour KPI Comparison — GET /sales/live/v2/labour-kpi-comparison
export const fetchLabourKpiComparisonRequest = (params: { locationId: string; startDate?: string; endDate?: string; employeeId?: string }) => ({
  type: T.FETCH_LABOUR_KPI_COMPARISON_REQUEST as typeof T.FETCH_LABOUR_KPI_COMPARISON_REQUEST,
  payload: params,
});

export const fetchLabourKpiComparisonSuccess = (data: LabourKpiComparisonResponse) => ({
  type: T.FETCH_LABOUR_KPI_COMPARISON_SUCCESS as typeof T.FETCH_LABOUR_KPI_COMPARISON_SUCCESS,
  payload: data,
});

export const fetchLabourKpiComparisonFailure = (error: string) => ({
  type: T.FETCH_LABOUR_KPI_COMPARISON_FAILURE as typeof T.FETCH_LABOUR_KPI_COMPARISON_FAILURE,
  payload: { error },
});

// Labour Employees — GET /sales/labour/employees
export const fetchLabourEmployeesRequest = (params: { locationId: string }) => ({
  type: T.FETCH_LABOUR_EMPLOYEES_REQUEST as typeof T.FETCH_LABOUR_EMPLOYEES_REQUEST,
  payload: params,
});

export const fetchLabourEmployeesSuccess = (data: LabourEmployeesResponse) => ({
  type: T.FETCH_LABOUR_EMPLOYEES_SUCCESS as typeof T.FETCH_LABOUR_EMPLOYEES_SUCCESS,
  payload: data,
});

export const fetchLabourEmployeesFailure = (error: string) => ({
  type: T.FETCH_LABOUR_EMPLOYEES_FAILURE as typeof T.FETCH_LABOUR_EMPLOYEES_FAILURE,
  payload: { error },
});

// Labour Staff Performance — GET /sales/live/v2/labour-staff-performance
// Labour Employee Sales (KPI) — GET /sales/live/v2/labour-employee-sales
export const fetchLabourEmployeeSalesRequest = (params: { locationId: string; startDate?: string; endDate?: string; employeeId?: string }) => ({
  type: T.FETCH_LABOUR_EMPLOYEE_SALES_REQUEST as typeof T.FETCH_LABOUR_EMPLOYEE_SALES_REQUEST,
  payload: params,
});

export const fetchLabourEmployeeSalesSuccess = (data: LabourEmployeeSalesResponse) => ({
  type: T.FETCH_LABOUR_EMPLOYEE_SALES_SUCCESS as typeof T.FETCH_LABOUR_EMPLOYEE_SALES_SUCCESS,
  payload: data,
});

export const fetchLabourEmployeeSalesFailure = (error: string) => ({
  type: T.FETCH_LABOUR_EMPLOYEE_SALES_FAILURE as typeof T.FETCH_LABOUR_EMPLOYEE_SALES_FAILURE,
  payload: { error },
});

// Labour Working Hours (KPI) — GET /sales/live/v2/labour-working-hours
export const fetchLabourWorkingHoursRequest = (params: { locationId: string; startDate?: string; endDate?: string; employeeId?: string }) => ({
  type: T.FETCH_LABOUR_WORKING_HOURS_REQUEST as typeof T.FETCH_LABOUR_WORKING_HOURS_REQUEST,
  payload: params,
});

export const fetchLabourWorkingHoursSuccess = (data: LabourWorkingHoursResponse) => ({
  type: T.FETCH_LABOUR_WORKING_HOURS_SUCCESS as typeof T.FETCH_LABOUR_WORKING_HOURS_SUCCESS,
  payload: data,
});

export const fetchLabourWorkingHoursFailure = (error: string) => ({
  type: T.FETCH_LABOUR_WORKING_HOURS_FAILURE as typeof T.FETCH_LABOUR_WORKING_HOURS_FAILURE,
  payload: { error },
});

export const fetchLabourStaffPerformanceRequest = (params: { locationId: string; page?: number; size?: number; search?: string; startDate?: string; endDate?: string; employeeId?: string; sortBy?: string; sortAsc?: boolean }) => ({
  type: T.FETCH_LABOUR_STAFF_PERFORMANCE_REQUEST as typeof T.FETCH_LABOUR_STAFF_PERFORMANCE_REQUEST,
  payload: params,
});

export const fetchLabourStaffPerformanceSuccess = (data: LabourStaffPerformanceResponse) => ({
  type: T.FETCH_LABOUR_STAFF_PERFORMANCE_SUCCESS as typeof T.FETCH_LABOUR_STAFF_PERFORMANCE_SUCCESS,
  payload: data,
});

export const fetchLabourStaffPerformanceFailure = (error: string) => ({
  type: T.FETCH_LABOUR_STAFF_PERFORMANCE_FAILURE as typeof T.FETCH_LABOUR_STAFF_PERFORMANCE_FAILURE,
  payload: { error },
});

// Labour Working Hours Log — GET /sales/live/v2/labour-working-hours-log
export const fetchLabourWorkingHoursLogRequest = (params: { locationId: string; page?: number; size?: number; search?: string; startDate?: string; endDate?: string; employeeId?: string; sortBy?: string; sortAsc?: boolean }) => ({
  type: T.FETCH_LABOUR_WORKING_HOURS_LOG_REQUEST as typeof T.FETCH_LABOUR_WORKING_HOURS_LOG_REQUEST,
  payload: params,
});

export const fetchLabourWorkingHoursLogSuccess = (data: LabourWorkingHoursLogResponse) => ({
  type: T.FETCH_LABOUR_WORKING_HOURS_LOG_SUCCESS as typeof T.FETCH_LABOUR_WORKING_HOURS_LOG_SUCCESS,
  payload: data,
});

export const fetchLabourWorkingHoursLogFailure = (error: string) => ({
  type: T.FETCH_LABOUR_WORKING_HOURS_LOG_FAILURE as typeof T.FETCH_LABOUR_WORKING_HOURS_LOG_FAILURE,
  payload: { error },
});

// Sales by Channel — Financial Breakdown (ZAR-1555) — GET /sales/live/v2/sales-by-channel-breakdown
// Today passes no dates; historical ranges pass startDate + endDate.
export const fetchSalesByChannelBreakdownRequest = (params: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource }) => ({
  type: T.FETCH_SALES_BY_CHANNEL_BREAKDOWN_REQUEST as typeof T.FETCH_SALES_BY_CHANNEL_BREAKDOWN_REQUEST,
  payload: params,
});

export const fetchSalesByChannelBreakdownSuccess = (data: SalesByChannelBreakdownResponse) => ({
  type: T.FETCH_SALES_BY_CHANNEL_BREAKDOWN_SUCCESS as typeof T.FETCH_SALES_BY_CHANNEL_BREAKDOWN_SUCCESS,
  payload: data,
});

export const fetchSalesByChannelBreakdownFailure = (error: string) => ({
  type: T.FETCH_SALES_BY_CHANNEL_BREAKDOWN_FAILURE as typeof T.FETCH_SALES_BY_CHANNEL_BREAKDOWN_FAILURE,
  payload: { error },
});

// Sales by Channel / Source card (ZAR-2371) — Source view rows. Pass the SELECTED range
// (Today: no dates); the saga adds the comparison window and `type=source`.
export const fetchLiveBySourceRequest = (params: { locationId: string; startDate?: string; endDate?: string; orderSource?: OrderSource }) => ({
  type: T.FETCH_LIVE_BY_SOURCE_REQUEST as typeof T.FETCH_LIVE_BY_SOURCE_REQUEST,
  payload: params,
});

export const fetchLiveBySourceSuccess = (data: LiveByChannelItem[]) => ({
  type: T.FETCH_LIVE_BY_SOURCE_SUCCESS as typeof T.FETCH_LIVE_BY_SOURCE_SUCCESS,
  payload: data,
});

export const fetchLiveBySourceFailure = (error: string) => ({
  type: T.FETCH_LIVE_BY_SOURCE_FAILURE as typeof T.FETCH_LIVE_BY_SOURCE_FAILURE,
  payload: { error },
});

export type ClickHouseAction =
  | ReturnType<typeof fetchClickHouseRequest>
  | ReturnType<typeof fetchClickHouseSuccess>
  | ReturnType<typeof fetchClickHouseFailure>
  | ReturnType<typeof fetchSnapshotDetailsRequest>
  | ReturnType<typeof fetchSnapshotDetailsSuccess>
  | ReturnType<typeof fetchSnapshotDetailsFailure>
  | ReturnType<typeof fetchLiveItemSalesV2Request>
  | ReturnType<typeof fetchLiveItemSalesV2Success>
  | ReturnType<typeof fetchLiveItemSalesV2Failure>
  | ReturnType<typeof fetchLiveByChannelRequest>
  | ReturnType<typeof fetchLiveByChannelSuccess>
  | ReturnType<typeof fetchLiveByChannelFailure>
  | ReturnType<typeof fetchThirdPartyByChannelRequest>
  | ReturnType<typeof fetchThirdPartyByChannelSuccess>
  | ReturnType<typeof fetchThirdPartyByChannelFailure>
  | ReturnType<typeof fetchLivePaymentsRequest>
  | ReturnType<typeof fetchLivePaymentsSuccess>
  | ReturnType<typeof fetchLivePaymentsFailure>
  | ReturnType<typeof fetchLiveRefundSummaryRequest>
  | ReturnType<typeof fetchLiveRefundSummarySuccess>
  | ReturnType<typeof fetchLiveRefundSummaryFailure>
  | ReturnType<typeof fetchLiveRefundDetailsRequest>
  | ReturnType<typeof fetchLiveRefundDetailsSuccess>
  | ReturnType<typeof fetchLiveRefundDetailsFailure>
  | ReturnType<typeof fetchRefundLogRequest>
  | ReturnType<typeof fetchRefundLogSuccess>
  | ReturnType<typeof fetchRefundLogFailure>
  | ReturnType<typeof fetchRefundReasonsRequest>
  | ReturnType<typeof fetchRefundReasonsSuccess>
  | ReturnType<typeof fetchRefundReasonsFailure>
  | ReturnType<typeof fetchRefundByStaffRequest>
  | ReturnType<typeof fetchRefundByStaffSuccess>
  | ReturnType<typeof fetchRefundByStaffFailure>
  | ReturnType<typeof fetchRefundComponentSplitRequest>
  | ReturnType<typeof fetchRefundComponentSplitSuccess>
  | ReturnType<typeof fetchRefundComponentSplitFailure>
  | ReturnType<typeof fetchLiveVoidSummaryRequest>
  | ReturnType<typeof fetchLiveVoidSummarySuccess>
  | ReturnType<typeof fetchLiveVoidSummaryFailure>
  | ReturnType<typeof fetchVoidByStaffRequest>
  | ReturnType<typeof fetchVoidByStaffSuccess>
  | ReturnType<typeof fetchVoidByStaffFailure>
  | ReturnType<typeof fetchVoidKpisRequest>
  | ReturnType<typeof fetchVoidKpisSuccess>
  | ReturnType<typeof fetchVoidKpisFailure>
  | ReturnType<typeof fetchLiveVoidDetailsRequest>
  | ReturnType<typeof fetchLiveVoidDetailsSuccess>
  | ReturnType<typeof fetchLiveVoidDetailsFailure>
  | ReturnType<typeof fetchLiveHourlySalesRequest>
  | ReturnType<typeof fetchLiveHourlySalesSuccess>
  | ReturnType<typeof fetchLiveHourlySalesFailure>
  | ReturnType<typeof fetchHourlySalesRequest>
  | ReturnType<typeof fetchHourlySalesSuccess>
  | ReturnType<typeof fetchHourlySalesFailure>
  | ReturnType<typeof fetchLiveStaffPerformanceRequest>
  | ReturnType<typeof fetchLiveStaffPerformanceSuccess>
  | ReturnType<typeof fetchLiveStaffPerformanceFailure>
  | ReturnType<typeof fetchLiveStaffActiveClockingRequest>
  | ReturnType<typeof fetchLiveStaffActiveClockingSuccess>
  | ReturnType<typeof fetchLiveStaffActiveClockingFailure>
  | ReturnType<typeof fetchLiveStaffActiveClockingV2Request>
  | ReturnType<typeof fetchLiveStaffActiveClockingV2Success>
  | ReturnType<typeof fetchLiveStaffActiveClockingV2Failure>
  | ReturnType<typeof fetchOrderLogRequest>
  | ReturnType<typeof fetchOrderLogSuccess>
  | ReturnType<typeof fetchOrderLogFailure>
  | ReturnType<typeof fetchCancelledOrderLogRequest>
  | ReturnType<typeof fetchCancelledOrderLogSuccess>
  | ReturnType<typeof fetchCancelledOrderLogFailure>
  | ReturnType<typeof exportOrderLogRequest>
  | ReturnType<typeof exportOrderLogSuccess>
  | ReturnType<typeof exportOrderLogFailure>
  | ReturnType<typeof exportOrderLogReset>
  | ReturnType<typeof fetchOrderDetailRequest>
  | ReturnType<typeof fetchOrderDetailSuccess>
  | ReturnType<typeof fetchOrderDetailFailure>
  | ReturnType<typeof clearOrderDetail>
  | ReturnType<typeof fetchLiveUnpaidOrdersRequest>
  | ReturnType<typeof fetchLiveUnpaidOrdersSuccess>
  | ReturnType<typeof fetchLiveUnpaidOrdersFailure>
  | ReturnType<typeof fetchLiveUnpaidLogRequest>
  | ReturnType<typeof fetchLiveUnpaidLogSuccess>
  | ReturnType<typeof fetchLiveUnpaidLogFailure>
  | ReturnType<typeof fetchLiveCompOrderLogRequest>
  | ReturnType<typeof fetchLiveCompOrderLogSuccess>
  | ReturnType<typeof fetchLiveCompOrderLogFailure>
  | ReturnType<typeof fetchLiveCompReasonsRequest>
  | ReturnType<typeof fetchLiveCompReasonsSuccess>
  | ReturnType<typeof fetchLiveCompReasonsFailure>
  | ReturnType<typeof fetchLiveCompByStaffRequest>
  | ReturnType<typeof fetchLiveCompByStaffSuccess>
  | ReturnType<typeof fetchLiveCompByStaffFailure>
  | ReturnType<typeof fetchLiveCategorySalesRequest>
  | ReturnType<typeof fetchLiveCategorySalesSuccess>
  | ReturnType<typeof fetchLiveCategorySalesFailure>
  | ReturnType<typeof fetchLiveStaffRefundsRequest>
  | ReturnType<typeof fetchLiveStaffRefundsSuccess>
  | ReturnType<typeof fetchLiveStaffRefundsFailure>
  | ReturnType<typeof fetchSnapshotComparisonRequest>
  | ReturnType<typeof fetchSnapshotComparisonSuccess>
  | ReturnType<typeof fetchSnapshotComparisonFailure>
  | ReturnType<typeof fetchChannelNamesRequest>
  | ReturnType<typeof fetchChannelNamesSuccess>
  | ReturnType<typeof fetchChannelNamesFailure>
  | ReturnType<typeof fetchLiveOutstandingValueRequest>
  | ReturnType<typeof fetchLiveOutstandingValueSuccess>
  | ReturnType<typeof fetchLiveOutstandingValueFailure>
  | ReturnType<typeof fetchLabourEmployeeSalesRequest>
  | ReturnType<typeof fetchLabourEmployeeSalesSuccess>
  | ReturnType<typeof fetchLabourEmployeeSalesFailure>
  | ReturnType<typeof fetchLabourWorkingHoursRequest>
  | ReturnType<typeof fetchLabourWorkingHoursSuccess>
  | ReturnType<typeof fetchLabourWorkingHoursFailure>
  | ReturnType<typeof fetchLabourStaffPerformanceRequest>
  | ReturnType<typeof fetchLabourStaffPerformanceSuccess>
  | ReturnType<typeof fetchLabourStaffPerformanceFailure>
  | ReturnType<typeof fetchLabourWorkingHoursLogRequest>
  | ReturnType<typeof fetchLabourWorkingHoursLogSuccess>
  | ReturnType<typeof fetchLabourWorkingHoursLogFailure>
  | ReturnType<typeof fetchLabourTipsRequest>
  | ReturnType<typeof fetchLabourTipsSuccess>
  | ReturnType<typeof fetchLabourTipsFailure>
  | ReturnType<typeof fetchLabourVoidsRequest>
  | ReturnType<typeof fetchLabourVoidsSuccess>
  | ReturnType<typeof fetchLabourVoidsFailure>
  | ReturnType<typeof fetchLabourKpiComparisonRequest>
  | ReturnType<typeof fetchLabourKpiComparisonSuccess>
  | ReturnType<typeof fetchLabourKpiComparisonFailure>
  | ReturnType<typeof fetchLabourEmployeesRequest>
  | ReturnType<typeof fetchLabourEmployeesSuccess>
  | ReturnType<typeof fetchLabourEmployeesFailure>
  | ReturnType<typeof fetchSalesByChannelBreakdownRequest>
  | ReturnType<typeof fetchSalesByChannelBreakdownSuccess>
  | ReturnType<typeof fetchSalesByChannelBreakdownFailure>
  | ReturnType<typeof fetchLiveBySourceRequest>
  | ReturnType<typeof fetchLiveBySourceSuccess>
  | ReturnType<typeof fetchLiveBySourceFailure>;
